import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import {
  editProfile,
  changePasswordRequest,
  clearProfileMessages,
  verifyEmailRequest,
  getAdminProfileRequest,
  updateLogoRequest,
  resetPasswordRequest,
} from "../../redux/actions/profileActions";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { BiEdit } from "react-icons/bi";
import { MdVerified } from "react-icons/md";
import { FaCamera } from "react-icons/fa";
import ChangeMailModal from "../../modals/ChangeMailModal";
import "./Profile.css";

const Profile = () => {
  const { user } = useSelector((state) => state.auth);
  // Profile form
  const {
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    register: registerProfile,
    setValue: setProfileValue,
    reset: resetProfile,
    watch: watchProfile,
  } = useForm({});
  const id = user?._id;
  // Password form
  const {
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    register: registerPassword,
    watch: watchPassword,
  } = useForm({});

  const newPassword = watchPassword("newPassword");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();
  const {
    loading: profileLoading,
    error,
    success,
  } = useSelector((state) => state.profile);
  const { loading2: profileLoading2, profileData } = useSelector(
    (state) => state.profile,
  );

  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getAdminProfileRequest(id));
    }
  }, [dispatch, id]);

  // Set form values when profile data loads
  useEffect(() => {
    if (profileData) {
      setProfileValue("firstName", profileData.firstName || "");
      setProfileValue("lastName", profileData.lastName || "");
      setProfileValue("email", profileData.email || "");
    }
  }, [profileData, setProfileValue]);

  const onProfileSubmit = (data) => {
    setIsProfileSubmitting(true);

    const profileUpdateData = {
      userId: id,
      firstName: data.firstName,
      lastName: data.lastName || "",
    };
    dispatch(editProfile(profileUpdateData));
  };

  const onPasswordSubmit = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      return;
    }

    setIsPasswordSubmitting(true);
    const passwordData = {
      userId: id,
      oldPassword: data.currentPassword,
      newPassword: data.newPassword,
    };
    dispatch(resetPasswordRequest(passwordData));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLogoUploading(true);

    const reader = new FileReader();
    reader.onload = () => {
      const logoData = {
        logo: reader.result,
        fileName: file.name,
        fileType: file.type,
        userId: id,
      };
      dispatch(updateLogoRequest(logoData));
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (success) {
      setIsProfileSubmitting(false);
      setIsPasswordSubmitting(false);
      setIsLogoUploading(false);
      setIsResettingPassword(false);
      // Only fetch updated profile data after profile or logo update, not password change
      if (id && (isProfileSubmitting || isLogoUploading)) {
        dispatch(getAdminProfileRequest(id));
      }
      setTimeout(() => dispatch(clearProfileMessages()), 100);
    }
    if (error) {
      setIsProfileSubmitting(false);
      setIsPasswordSubmitting(false);
      setIsLogoUploading(false);
      setIsResettingPassword(false);
      setTimeout(() => dispatch(clearProfileMessages()), 100);
    }
  }, [success, error, dispatch, id, isProfileSubmitting, isLogoUploading]);

  return (
    <div className="page-container">
      <div className="page-card scroll-container" style={{ padding: "0" }}>
        {profileLoading2 && !profileData ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="text-center">
              <div
                className="spinner-border text-primary mb-3"
                role="status"
                style={{ width: "3rem", height: "3rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">Loading profile...</p>
            </div>
          </div>
        ) : (
          <div className="container-fluid px-4 py-3">
            {/* Modern Profile Header */}
            <div className="profile-hero-section mb-4">
              <div className="profile-cover">
                <div className="profile-info-wrapper">
                  <div className="profile-avatar-section">
                    <div className="avatar-container">
                      {profileData?.logo ? (
                        <div className="profile-avatar d-flex align-items-center justify-content-center">
                          <img
                            src={profileData.logo}
                            alt="Profile"
                            className=""
                            style={{ width: "80px", height: "60px" }}
                            onError={(e) => {
                              console.error(
                                "Logo failed to load:",
                                profileData.logo,
                              );
                            }}
                          />
                        </div>
                      ) : (
                        <div className="profile-avatar-placeholder">
                          <span className="avatar-initials">
                            {profileData?.firstName?.[0]}
                            {profileData?.lastName?.[0]}
                          </span>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: "none" }}
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload" className="avatar-edit-btn">
                        {isLogoUploading ? (
                          <div
                            className="spinner-border spinner-border-sm text-white"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <FaCamera size={12} />
                        )}
                      </label>
                    </div>
                  </div>
                  <div className="profile-details">
                    <h2 className="profile-name d-flex align-items-center justify-content-center">
                      {profileData?.firstName} {profileData?.lastName}{" "}
                      <span>
                        <MdVerified className="text-success ms-1" />
                      </span>
                    </h2>
                    <p className="profile-role">
                      {profileData?.roleId?.name || "Admin"}
                    </p>
                    {/* <div className="profile-stats">
                      <div className="stat-item">
                        <span>Verified Account</span>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>

            <Row className="g-4">
              <Col lg={8}>
                {/* Personal Information Card */}
                <Card className="modern-card mb-4">
                  <Card.Header className="modern-card-header">
                    <h5 className="card-title mb-0">
                      <BiEdit className="me-2" />
                      Personal Information
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-4" style={{ height: "240px" }}>
                    <Form onSubmit={handleProfileSubmit(onProfileSubmit)}>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="modern-label">
                              First Name<span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              className="modern-input"
                              {...registerProfile("firstName", {
                                required: "First name is required",
                              })}
                              type="text"
                              placeholder="Enter first name"
                            />
                            {profileErrors.firstName && (
                              <div className="invalid-feedback d-block">
                                {profileErrors.firstName.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="modern-label">
                              Last Name
                            </Form.Label>
                            <Form.Control
                              className="modern-input"
                              {...registerProfile("lastName")}
                              type="text"
                              placeholder="Enter last name"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label className="modern-label">
                              Email Address
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="input-group">
                              <Form.Control
                                className="modern-input"
                                {...registerProfile("email")}
                                type="email"
                                placeholder="Enter email"
                                disabled
                              />
                              <div className="input-group-text bg-white border-start-0">
                                <MdVerified className="text-success me-1" />
                                <BiEdit
                                  className="text-muted cursor-pointer"
                                  onClick={() => setShowEmailModal(true)}
                                  title="Change Email"
                                />
                              </div>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-end mt-4">
                        <Button
                          type="submit"
                          disabled={isProfileSubmitting}
                          className="profile-action-btn"
                        >
                          {isProfileSubmitting ? (
                            <>
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Updating...
                            </>
                          ) : (
                            "Update Profile"
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                {/* Account Security Card */}
                <Card className="modern-card mb-4">
                  <Card.Header className="modern-card-header">
                    <h5 className="card-title mb-0">
                      <LuEye className="me-2" />
                      Account Security
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-4" style={{ height: "240px" }}>
                    <div className="security-item mb-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6 className="mb-1">Email Verification</h6>
                          <small className="text-muted">
                            Your email is verified
                          </small>
                        </div>
                        <MdVerified className="text-success" size={20} />
                      </div>
                    </div>
                    <div className="security-item">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6 className="mb-1">Password</h6>
                          <small className="text-muted">
                            Last changed recently
                          </small>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="modern-btn-outline"
                          onClick={() => {
                            document
                              .getElementById("password-section")
                              .scrollIntoView({ behavior: "smooth" });
                            setTimeout(() => {
                              const currentPasswordInput =
                                document.querySelector(
                                  'input[name="currentPassword"]',
                                );
                              if (currentPasswordInput) {
                                currentPasswordInput.focus();
                              }
                            }, 500);
                          }}
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Change Password Section */}
            <Row className="g-4" id="password-section">
              <Col lg={8}>
                <Card className="modern-card">
                  <Card.Header className="modern-card-header">
                    <h5 className="card-title mb-0">
                      <LuEyeOff className="me-2" />
                      Change Password
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label className="modern-label">
                              Current Password
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                className="modern-input"
                                {...registerPassword("currentPassword", {
                                  required: "Current password is required",
                                })}
                                type={showCurrentPassword ? "text" : "password"}
                                placeholder="Enter current password"
                                style={{ paddingRight: "45px" }}
                                onKeyDown={(e) =>
                                  (e.key === "Enter" || e.key === " ") &&
                                  e.preventDefault()
                                }
                              />
                              <Button
                                variant="link"
                                type="button"
                                className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted"
                                style={{ right: "10px", zIndex: 5 }}
                                onClick={() =>
                                  setShowCurrentPassword(!showCurrentPassword)
                                }
                              >
                                {showCurrentPassword ? <LuEyeOff /> : <LuEye />}
                              </Button>
                            </div>
                            {passwordErrors.currentPassword && (
                              <div className="invalid-feedback d-block">
                                {passwordErrors.currentPassword.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="modern-label">
                              New Password<span className="text-danger">*</span>
                            </Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                className="modern-input"
                                {...registerPassword("newPassword", {
                                  required: "New password is required",
                                  minLength: {
                                    value: 8,
                                    message:
                                      "Password must be at least 8 characters",
                                  },
                                  validate: {
                                    hasUpperCase: (value) =>
                                      /[A-Z]/.test(value) ||
                                      "Password must contain at least one uppercase letter",
                                    hasLowerCase: (value) =>
                                      /[a-z]/.test(value) ||
                                      "Password must contain at least one lowercase letter",
                                    hasNumber: (value) =>
                                      /[0-9]/.test(value) ||
                                      "Password must contain at least one number",
                                    hasSpecialChar: (value) =>
                                      /[!@#$%^&*(),.?":{}|<>]/.test(value) ||
                                      "Password must contain at least one special character",
                                  },
                                })}
                                type={showNewPassword ? "text" : "password"}
                                placeholder="Enter new password"
                                style={{ paddingRight: "45px" }}
                                onKeyDown={(e) =>
                                  (e.key === "Enter" || e.key === " ") &&
                                  e.preventDefault()
                                }
                              />
                              <Button
                                variant="link"
                                type="button"
                                className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted"
                                style={{ right: "10px", zIndex: 5 }}
                                onClick={() =>
                                  setShowNewPassword(!showNewPassword)
                                }
                              >
                                {showNewPassword ? <LuEyeOff /> : <LuEye />}
                              </Button>
                            </div>
                            {passwordErrors.newPassword && (
                              <div className="invalid-feedback d-block">
                                {passwordErrors.newPassword.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="modern-label">
                              Confirm New Password
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                className="modern-input"
                                {...registerPassword("confirmPassword", {
                                  required: "Please confirm your password",
                                  validate: (value) =>
                                    value === newPassword ||
                                    "Passwords do not match",
                                })}
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm new password"
                                style={{ paddingRight: "45px" }}
                                onKeyDown={(e) =>
                                  (e.key === "Enter" || e.key === " ") &&
                                  e.preventDefault()
                                }
                              />
                              <Button
                                variant="link"
                                type="button"
                                className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted"
                                style={{ right: "10px", zIndex: 5 }}
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                              >
                                {showConfirmPassword ? <LuEyeOff /> : <LuEye />}
                              </Button>
                            </div>
                            {passwordErrors.confirmPassword && (
                              <div className="invalid-feedback d-block">
                                {passwordErrors.confirmPassword.message}
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-end mt-4">
                        <Button
                          type="submit"
                          disabled={isPasswordSubmitting}
                          className="profile-action-btn"
                        >
                          {isPasswordSubmitting ? (
                            <>
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Changing...
                            </>
                          ) : (
                            "Change Password"
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </div>

      {showEmailModal && (
        <ChangeMailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          currentEmail={profileData?.email}
          userId={id}
        />
      )}
    </div>
  );
};

export default Profile;
